import type {Moment} from 'moment-timezone';

import {type AutomaticPricing} from 'web-app/react/profile/child-profile/plans-and-invoices/ad-hoc-purchases/hooks/use-get-automatic-pricing-for-purchases';
import {getPricingGroupIdFromRuleGroupIdOnDate} from 'web-app/react/profile/child-profile/plans-and-invoices/ad-hoc-purchases/utils';
import type {ChildPricingData} from 'web-app/react/profile/child-profile/plans-and-invoices/ad-hoc-purchases/utils/types';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import {AUTOMATIC_PRICING_GROUP_ID} from '../constants';
import type * as Types from '../types';

type Args = {
    selectedChildIds: Array<Types.State['childrenState']['selectedChildren'][number]['id']>;
    selectedPricingGroupId: Types.State['pricingGroupState']['selectedPricingGroup'];
    customPeriodStartDate: null | Moment;
    customPeriodEndDate: null | Moment;
    automaticRuleGroupId: string | null;
    automaticPricings: Array<AutomaticPricing>;
};

type AdhocPurchasePricingGroups = {
    pricingGroupsByChildrenForStartDate: Array<PricingGroupsForChild>;
    pricingGroupsByChildrenForEndDate: Array<PricingGroupsForChild>;
};

type PricingGroupsForChild = {
    childId: string;
    pricingGroupId: string;
};

const usePricingGroupsForAdhocPurchase = ({
    selectedChildIds,
    selectedPricingGroupId,
    customPeriodStartDate,
    customPeriodEndDate,
    automaticRuleGroupId,
    automaticPricings,
}: Args): null | AdhocPurchasePricingGroups => {
    if (!hasValue(selectedPricingGroupId)) {
        return null;
    }

    const isAutomaticPricingGroupSelected = selectedPricingGroupId === AUTOMATIC_PRICING_GROUP_ID;
    const pricingGroupId = isAutomaticPricingGroupSelected ? automaticRuleGroupId : selectedPricingGroupId;

    if (!hasValue(pricingGroupId)) {
        return null;
    }

    if (!isAutomaticPricingGroupSelected) {
        return {
            pricingGroupsByChildrenForEndDate: selectedChildIds.map(childId => ({childId, pricingGroupId})),
            pricingGroupsByChildrenForStartDate: selectedChildIds.map(childId => ({childId, pricingGroupId})),
        };
    }

    return automaticAdhocPurchasePricingGroupsByChildren(
        selectedChildIds,
        pricingGroupId,
        customPeriodStartDate,
        customPeriodEndDate,
        automaticPricings,
    );
};

export const automaticAdhocPurchasePricingGroupsByChildren = (
    selectedChildIds: Array<string>,
    pricingGroupId: string,
    customPeriodStartDate: Moment | null,
    customPeriodEndDate: Moment | null,
    automaticPricings: AutomaticPricing[],
) => {
    const pricingGroupsByChildrenForStartDate = automaticPricings
        .map(pricing => pricinGroupsByChildrenForDate(pricingGroupId, customPeriodStartDate, pricing))
        .filter(hasValue)
        .filter(pricingGroupForChild => selectedChildIds.includes(pricingGroupForChild.childId));

    const pricingGroupsByChildrenForEndDate = automaticPricings
        .map(pricing => pricinGroupsByChildrenForDate(pricingGroupId, customPeriodEndDate, pricing))
        .filter(hasValue)
        .filter(pricingGroupForChild => selectedChildIds.includes(pricingGroupForChild.childId));

    return {
        pricingGroupsByChildrenForEndDate,
        pricingGroupsByChildrenForStartDate,
    };
};

const pricinGroupsByChildrenForDate = (
    pricingGroupId: NonNullable<Types.State['pricingGroupState']['selectedPricingGroup']>,
    date: null | Moment,
    childPricingData: ChildPricingData,
) => {
    const maybePricingGroupId = getPricingGroupIdFromRuleGroupIdOnDate(pricingGroupId, date, childPricingData);

    if (!hasValue(maybePricingGroupId)) {
        return null;
    }

    return {
        childId: childPricingData.childId,
        pricingGroupId: maybePricingGroupId,
    };
};

export {usePricingGroupsForAdhocPurchase};
export type {AdhocPurchasePricingGroups, PricingGroupsForChild};
